import { EntryPass } from '@/constants'
import { useToast } from './use-toast'
import { apiFetch } from '@/utils'
import { useCallback, useEffect, useState } from 'react'

export const useGetActiveEntryPass = (walletAddress: `0x${string}`) => {
  const { toast } = useToast()
  const [activeEntryPass, setActiveEntryPass] = useState<EntryPass | null>(null)
  const [isEntryPassLoading, setIsEntryPassLoading] = useState(true)
  const [hasFetched, setHasFetched] = useState(false)

  const fetchActiveEntryPass = useCallback(async () => {
    try {
      setIsEntryPassLoading(true)
      const response = await apiFetch(`/v1/entry-passes/${walletAddress}`)

      if (response.status === 404) {
        // No active pass — not an error
        setActiveEntryPass(null)
        return
      }

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(
          errorData.details ||
            errorData.message ||
            `HTTP error! status: ${response.status}`
        )
      }

      const data = await response.json()

      // Handle both direct pass object and wrapped { success, data } responses
      if (data && data.success !== undefined && data.data !== undefined) {
        setActiveEntryPass(data.data || null)
      } else {
        setActiveEntryPass(data || null)
      }
    } catch (error: any) {
      console.error('Error fetching active entry pass. Reason: ', error)
      setActiveEntryPass(null)
      toast({
        title: 'Can not fetch active entry pass',
        description:
          error?.message || 'An error occurred while fetching active entry pass'
      })
    } finally {
      setIsEntryPassLoading(false)
      setHasFetched(true)
    }
  }, [walletAddress, toast])

  useEffect(() => {
    if (walletAddress && !hasFetched) {
      fetchActiveEntryPass()
    }
  }, [walletAddress, hasFetched, fetchActiveEntryPass])

  return {
    activeEntryPass,
    isEntryPassLoading,
    refetch: () => {
      setHasFetched(false)
    }
  }
}
